import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar,
  Box, 
  Menu, 
  MenuItem, 
  Typography,
  Select,
  FormControl,
  Avatar,
  FormControlLabel,
  Switch,
  IconButton
} from '@mui/material';
import { ExitToApp, Menu as MenuIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const Navbar = ({ toggleSidebar }) => {
  const { logout, user, clientInfo } = useAuth();
  const navigate = useNavigate();
  const { mode, toggleTheme } = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/login');
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: 'background.default',
        color: 'text.primary',
        // width: '100%',
        ml: { xs: 0, md: '300px' },
        width: { xs: '100%', md: 'calc(100% - 300px)' },
        zIndex: (theme) => theme.zIndex.drawer,
        transition: 'background-color 0.3s ease, color 0.3s ease, margin-left 0.3s ease, width 0.3s ease',
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={toggleSidebar}
          sx={{ 
            mr: 2,
            display: { xs: 'block', md: 'none' }
          }}
        >
          <MenuIcon />
        </IconButton>

        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <FormControl 
            sx={{ 
              width: { xs: '140px', sm: 250 },
              order: { xs: 1, sm: 1 },
              mr: { sm: 2 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '18px',
                bgcolor: 'primary.main',
                height: '36px',
                '& fieldset': {
                  borderColor: '#E0E0E0',
                },
                '&:hover fieldset': {
                  borderColor: '#E0E0E0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#E0E0E0',
                },
              },
            }}
          >
            <Select
              value={user?.clientId || ''}
              displayEmpty
              sx={{
                '& .MuiSelect-select': {
                  color: '#FFFFFF',
                  fontWeight: 400,
                  pointerEvents: 'none',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  pointerEvents: 'none',
                },
                cursor: 'default',
              }}
            >
              <MenuItem value={user?.clientId}>
                {clientInfo ? (
                  <Box sx={{ 
                    display: { xs: 'block', sm: 'block' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {window.innerWidth < 600 
                      ? truncateText(clientInfo.name, 15) 
                      : clientInfo.name.split(' ').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                        ).join(' ')}
                  </Box>
                ) : 'Loading...'}
              </MenuItem>
            </Select>
          </FormControl>
          
          <Typography 
            variant="body1" 
            sx={{ 
              mx: 1,
              color: 'text.primary',
              order: { xs: 2, sm: 2 }
            }}
          >
            {(user?.name || 'User').split(' ').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            ).join(' ')}
          </Typography>
          
          <Avatar
            src={user?.image_url}
            alt={user?.name || 'User'}
            sx={{
              width: 40,
              height: 40,
              cursor: 'pointer',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              order: { xs: 3, sm: 3 }
            }}
            onClick={handleMenu}
          >
            {!user?.image_url && (user?.name?.charAt(0) || 'U')}
          </Avatar>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1,
                minWidth: 120,
                borderRadius: 2,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                },
              },
            }}
          >
            <MenuItem>
              <FormControlLabel
                control={
                  <Switch
                    checked={mode === 'dark'}
                    onChange={toggleTheme}
                    color="primary"
                  />
                }
                label={mode === 'dark' ? 'Dark Mode' : 'Light Mode'}
              />
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ExitToApp sx={{ mr: 1, fontSize: 20, color: 'error.main' }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
